import React from 'react'
import { useHistory, Prompt } from 'react-router'
import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'

import { ObjectType } from '../types'

import LoadingSpinner from '@SonarCX/components/atoms/LoadingSpinner'
import Button from '@SonarCX/components/atoms/Button2'
import CustomFields from './CustomFields'
import NewFieldModal from '../FieldModal/NewFieldModal'
import ErrorToast from '@SonarCX/components/molecules/ErrorToast'

import { separateFields, cleanList, checkErrors } from './helpers'
import {
  selectCustomFields,
  fetchCustomFields,
  createCustomFields,
  updateCustomFields
} from '@SonarCX/slices/customFields'
import { AddSuccessAlert } from '@SonarCX/slices/alert'
import { useAreYouSure } from '@SonarCX/providers/AreYouSureProvider'
import useStyles from './styles'

type CustomFieldsListProps = {
  tab: ObjectType
}

const initialChanged = {
  [ObjectType.ExternalUser]: false,
  [ObjectType.Task]: false,
  [ObjectType.User]: false
}

const CustomFieldsList: React.FC<CustomFieldsListProps> = ({ tab }) => {
  const history = useHistory()
  const dispatch = useAppDispatch()
  const {
    loaded,
    loading,
    customFields,
    error: selectorError
  } = useAppSelector(selectCustomFields)

  const { setAction, setReason, setV2 } = useAreYouSure()

  const [changedType, setChangedType] = React.useState(null)
  const [saving, setSaving] = React.useState(false)
  const [fields, setFields] = React.useState({
    [ObjectType.ExternalUser]: [],
    [ObjectType.Task]: [],
    [ObjectType.User]: []
  })
  const [triggerSave, setTriggerSave] = React.useState({
    [ObjectType.ExternalUser]: false,
    [ObjectType.Task]: false,
    [ObjectType.User]: false
  })
  const [newFieldModalOpen, setNewFieldModalOpen] = React.useState(false)
  const [search, setSearch] = React.useState('')
  const [error, setError] = React.useState(null)
  const [navigate, setNavigate] = React.useState(null)
  const [changed, _setChanged] = React.useState(initialChanged)
  const somethingChanged = React.useMemo(
    () => Object.values(changed).some(x => x),
    [changed]
  )
  const listTopRef = React.useRef<HTMLDivElement>(null)
  const [height, setHeight] = React.useState('67svh')

  const classes = useStyles()

  React.useEffect(() => {
    setSearch('')
  }, [tab, setSearch])

  const setChanged = React.useCallback(type => {
    _setChanged(old => ({ ...old, [type]: true }))
  }, [])

  // Performs api save
  const saveValues = React.useCallback(
    (fields, type) => {
      setError(null)

      let toPut = []
      let toPost = []

      // Separate values that need to be saved into new and updating
      Object.values(fields).forEach(list => {
        // @ts-ignore
        list.forEach(item => {
          if (item.dirty) {
            const pushList = item.new ? toPost : toPut
            pushList.push(item)
          }
        })
      })

      // Fix formatting for api
      toPut = cleanList(toPut)
      toPost = cleanList(toPost)

      // Verify data
      const error = checkErrors([...toPut, ...toPost])

      if (error) {
        setError({ err: { message: error } })
        return false
      }

      setSaving(true)

      // Perform save
      dispatch(
        createCustomFields(toPost, () => {
          dispatch(
            updateCustomFields(toPut, () => {
              setSaving(false)
              _setChanged(old => ({ ...old, [type]: false }))
              dispatch(AddSuccessAlert('Changes saved'))
            })
          )
        })
      )

      return true
    },
    [dispatch]
  )

  // Performs api save
  const saveDelete = React.useCallback(
    field => {
      setChangedType(field.object_type)
      setError(null)

      let toPut = [{ ...field }]

      // Fix formatting for api
      toPut = cleanList(toPut)

      // Verify data
      const error = checkErrors([...toPut])

      if (error) {
        setError({ err: { message: error } })
        return false
      }

      setSaving(true)

      // Perform save
      dispatch(
        updateCustomFields(toPut, () => {
          setSaving(false)
          _setChanged(old => ({ ...old, [field.object_type]: false }))
          dispatch(AddSuccessAlert('Changes Saved'))
        })
      )

      return true
    },
    [dispatch, setChangedType]
  )

  // Updates fields and starts a save
  const storeFields = React.useCallback(
    (type, values) => {
      setChangedType(type)
      const toSave = { ...fields, [type]: values }
      const result = saveValues(toSave, type)
      if (!result) {
        return
      }
      setFields(old => {
        const newVals = { ...old, [type]: values }
        return newVals
      })

      setChanged(type)
    },
    [fields, setChanged, saveValues]
  )

  // Load custom fields if needed
  React.useEffect(() => {
    if (!loaded && !loading) {
      dispatch(fetchCustomFields())
    }
  }, [loaded, loading, dispatch])

  React.useEffect(() => {
    if (!loaded) {
      return
    }

    const { external, task, user } = separateFields(customFields)

    if (!changedType) {
      setFields({
        [ObjectType.ExternalUser]: external,
        [ObjectType.Task]: task,
        [ObjectType.User]: user
      })
    } else {
      setFields(old => {
        const newVals = {
          ...old,
          [changedType]:
            changedType === ObjectType.ExternalUser
              ? external
              : changedType === ObjectType.Task
              ? task
              : user
        }
        return newVals
      })
    }
    //I dont need this to run on changedtype, only on customFields change.
    // eslint-disable-next-line
  }, [loaded, customFields])

  const resetError = React.useCallback(() => {
    setError(null)
  }, [])

  // Shows are you sure prompt before moving away
  const handleNavigation = React.useCallback(
    (location, action) => {
      const path = location.pathname

      if (somethingChanged) {
        setReason('You have unsaved changes')
        setAction(() => {
          _setChanged(initialChanged)
          setNavigate(path)
        })
        setV2(true)
        return false
      }

      return true
    },
    [somethingChanged, setReason, setAction, setV2]
  )

  // Navigates away from this page after user denies saving
  React.useEffect(() => {
    if (somethingChanged || !navigate) {
      return
    }

    history.push(navigate)
  }, [somethingChanged, navigate, history])

  React.useEffect(() => {
    const handleScroll = () => {
      if (!listTopRef.current) {
        return
      }

      const rect = listTopRef.current.getBoundingClientRect()
      setHeight(`calc(100svh - ${rect.top}px)`)
    }

    window.addEventListener('wheel', handleScroll)

    return () => {
      window.removeEventListener('wheel', handleScroll)
    }
  }, [])

  if (!loaded) {
    return <LoadingSpinner />
  }

  return (
    <>
      <div
        ref={listTopRef}
        className={classes.fieldsContainer}
        style={{ height }}
      >
        <div className={classes.fieldsTitleArea}>
          <div className={classes.fieldsTitle}>
            <span>Custom Fields</span>

            <Button
              className={classes.button}
              onClick={() => setNewFieldModalOpen(true)}
            >
              + Field
            </Button>
          </div>

          <input
            placeholder='Search'
            value={search}
            onChange={e => setSearch(e.target.value)}
            className={classes.fieldsSearch}
          />
        </div>

        <CustomFields
          initialData={fields[tab]}
          objectType={tab}
          storeFields={storeFields}
          saving={saving}
          changed={changed[tab]}
          setChanged={setChanged}
          triggerSave={triggerSave}
          setTriggerSave={setTriggerSave}
          saveDelete={saveDelete}
          search={search}
        />
      </div>

      {newFieldModalOpen && (
        <NewFieldModal
          objectType={tab}
          onClose={() => setNewFieldModalOpen(false)}
        />
      )}

      <Prompt message={handleNavigation} when />

      <ErrorToast resetError={resetError} error={error || selectorError} />
    </>
  )
}

export default CustomFieldsList
